import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { clearLastStudied, getLastStudied, setLastStudied } from "@/utils/lastStudied";
import { trackLessonStarted } from "@/features/analytics";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import TopBar from "@/components/top-bar";
import Icon from "@/components/icon";
import { type CourseContent, VitrinService } from "@/services/vitrin-service";
import type {
  VitrinCourseDetailLesson,
  VitrinCourseDetailLessonItem,
} from "@/models/course/VitrinCourseDetail";
import CoursePurchaseCta from "@/features/content/components/course-purchase-cta";
import LessonContentsV1 from "@/features/content/components/lesson-contents";
import LessonContentsV2 from "@/features/content-v2/components/lesson-contents";
import LessonNavigation from "@/features/content/components/lesson-navigation";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import {
  ProtectedContentNotice,
  useWindowSecure,
  useWindowSecureSupport,
} from "@/features/content-protection";

// Persisted preference for which lesson-content layout the user prefers.
type ContentView = "v1" | "v2";
const CONTENT_VIEW_KEY = "lesson-content-view";

// Icon shown on the toggle button = the view it switches *to*.
const CONTENT_VIEW_ICON: Record<ContentView, string> = {
  v1: "document-text",
  v2: "lessons",
};

const readContentView = (): ContentView => {
  if (typeof window === "undefined") return "v1";
  return window.localStorage.getItem(CONTENT_VIEW_KEY) === "v2" ? "v2" : "v1";
};

type NormalizedCourse = {
  id: number;
  title: string;
  description: string;
  users_count: number;
  lessons_count: number;
  is_free?: boolean;
  special_price?: number;
  price?: number;
  is_purchased?: boolean;
  is_enrolled?: boolean;
  settings?: {
    availability_status?: number;
    content_protection?: boolean;
  };
  mini_app?: {
    miniapp_eitaa_username?: string | null;
  };
};

const normalizeLessons = (raw: unknown): VitrinCourseDetailLesson[] => {
  const lessonItems: VitrinCourseDetailLessonItem[] = Array.isArray(raw)
    ? (raw as VitrinCourseDetailLessonItem[])
    : Array.isArray((raw as { data?: unknown[] } | undefined)?.data)
      ? ((raw as { data: unknown[] }).data as VitrinCourseDetailLessonItem[])
      : [];

  return lessonItems
    .map((item) => item?.data)
    .filter((lesson): lesson is VitrinCourseDetailLesson => Boolean(lesson))
    .sort(
      (a, b) =>
        (a.sort_order ?? Number.MAX_SAFE_INTEGER) -
        (b.sort_order ?? Number.MAX_SAFE_INTEGER),
    );
};

const normalizeContents = (raw: unknown): CourseContent[] => {
  if (Array.isArray(raw)) return raw as CourseContent[];
  const nested = (raw as { data?: unknown[] } | null)?.data;
  return Array.isArray(nested) ? (nested as CourseContent[]) : [];
};

// Paywall state: the lesson exists but the user hasn't purchased the course.
// Centered in the available space; the sticky purchase CTA sits below it.
function LessonLockedNotice() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center">
      {/* Layered lock badge with a soft glow + a premium star accent */}
      <div className="relative mb-6">
        <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl" />
        <div className="relative flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-inset ring-primary/15">
          <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Icon name="lock" size={30} className="text-primary" />
          </div>
        </div>
        <span className="absolute -right-1 -top-1 flex size-7 items-center justify-center rounded-full bg-warning shadow-md">
          <Icon name="star-fill" size={14} className="text-white" />
        </span>
      </div>

      <h2 className="text-lg font-bold text-foreground">این جلسه قفل است</h2>
      <p className="mt-2.5 max-w-[16rem] text-sm leading-7 text-default-500">
        برای دسترسی به محتوای این جلسه، دوره را تهیه کنید و یادگیری را ادامه دهید.
      </p>
    </div>
  );
}

const DEVICE_LIMIT_FALLBACK =
  "شما از حداکثر تعداد دستگاه‌های مجاز برای دسترسی به این دوره استفاده کرده‌اید.";

// The contents endpoint also answers 403 to users who *do* own the course when
// they exceed the allowed device count. Those users must see the server's own
// explanation — never the purchase CTA, which they already paid for.
function LessonDeviceLimitNotice({
  message,
  backHref,
}: {
  message?: string | null;
  backHref: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center">
      <div className="relative mb-6">
        <div className="absolute inset-0 rounded-full bg-warning/20 blur-2xl" />
        <div className="relative flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-warning/15 to-warning/5 ring-1 ring-inset ring-warning/15">
          <div className="flex size-16 items-center justify-center rounded-full bg-warning/10">
            <Icon name="phone" size={30} className="text-warning" />
          </div>
        </div>
        <span className="absolute -right-1 -top-1 flex size-7 items-center justify-center rounded-full bg-warning shadow-md">
          <Icon name="lock" size={14} className="text-white" />
        </span>
      </div>

      <h2 className="text-lg font-bold text-foreground">محدودیت تعداد دستگاه</h2>
      <p className="mt-2.5 max-w-[18rem] text-sm leading-7 text-default-500">
        {message || DEVICE_LIMIT_FALLBACK}
      </p>
      <p className="mt-1.5 max-w-[18rem] text-xs leading-6 text-default-400">
        برای ادامه، از یکی از دستگاه‌های قبلی خارج شوید یا با پشتیبانی تماس بگیرید.
      </p>

      <Link
        to={backHref}
        className="mt-6 rounded-lg bg-content3 px-5 py-3 text-sm font-medium text-default-700"
      >
        بازگشت به دوره
      </Link>
    </div>
  );
}

// The bar is the same on both sizes — only the room it gets differs. Desktop has
// the width to also carry the lesson's place in the course and the prev/next
// pair, so neither has to float over the content there.
const LESSON_BAR_CLASS = "lg:h-16 lg:gap-3 lg:px-6";

export default function CourseLessonPage() {
  const params = useParams<{ courseId: string; lessonId: string }>();
  const [searchParams] = useSearchParams();
  const miniAppUuid = searchParams.get("miniAppUuid") || "";
  const courseId = params?.courseId || "";
  const lessonId = params?.lessonId || "";
  const backHref = `/course/${courseId}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`;
  const canFetch = Boolean(miniAppUuid && courseId && lessonId);
  const [isFetchingMoreManually, setIsFetchingMoreManually] = useState(false);
  const [contentView, setContentView] = useState<ContentView>(readContentView);
  // The desktop layout *is* the playlist one — its stage-and-rail split already
  // puts every content of the lesson on screen at once, which is all the v1
  // stack had to offer. So the choice, and its toggle, stay a phone affair.
  const isDesktop = useIsDesktop();
  const view: ContentView = isDesktop ? "v2" : contentView;

  const handleChangeContentView = (view: ContentView) => {
    setContentView(view);
    try {
      window.localStorage.setItem(CONTENT_VIEW_KEY, view);
    } catch {
      // Ignore storage errors (e.g. private mode); the in-memory state still applies.
    }
  };

  const { data: courseRes, isLoading: isCourseLoading } = useQuery({
    queryKey: ["vitrin-course", miniAppUuid, courseId],
    queryFn: () => VitrinService.getCourseDetail(miniAppUuid, courseId),
    enabled: canFetch,
  });

  const { data: lessonsRes } = useQuery({
    queryKey: ["vitrin-course-lessons", miniAppUuid, courseId],
    queryFn: () =>
      VitrinService.getCourseLessons(miniAppUuid, courseId, {
        page: 1,
        perPage: 500,
      }),
    enabled: canFetch,
  });

  const contentsQuery = useInfiniteQuery({
    queryKey: ["vitrin-lesson-contents", miniAppUuid, courseId, lessonId],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      VitrinService.getLessonContents(miniAppUuid, courseId, lessonId, {
        page: pageParam,
        perPage: 20,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.status === 403) return undefined;
      const pagination = lastPage.pagination;
      if (!pagination) return undefined;
      if (pagination.current_page >= pagination.last_page) return undefined;
      return pagination.current_page + 1;
    },
    enabled: canFetch,
    retry: false,
  });

  const course = useMemo<NormalizedCourse | null>(() => {
    const raw = courseRes?.data as { course?: unknown } | undefined;
    if (!raw) return null;
    const candidate = (raw.course ?? raw) as Partial<NormalizedCourse> | null;
    if (!candidate || typeof candidate.id !== "number") return null;

    return {
      id: candidate.id,
      title: candidate.title ?? "دوره",
      description: candidate.description ?? "",
      users_count: candidate.users_count ?? 0,
      lessons_count: candidate.lessons_count ?? 0,
      is_free: candidate.is_free,
      special_price: candidate.special_price,
      price: candidate.price,
      is_purchased: candidate.is_purchased,
      is_enrolled: candidate.is_enrolled,
      settings: candidate.settings,
      mini_app: candidate.mini_app,
    };
  }, [courseRes?.data]);

  // Course with content_protection: keep the host's capture block (FLAG_SECURE)
  // on for the whole time this page is mounted; released on unmount. Hosts
  // that cannot honour the block (old Eitaa, non-Android) get the content
  // withheld instead — see the early return below.
  const contentProtection = Boolean(course?.settings?.content_protection);
  const secureSupport = useWindowSecureSupport();
  const protectionBlockReason =
    contentProtection &&
    (secureSupport === "outdated" || secureSupport === "unsupported-platform" || secureSupport === "unavailable")
      ? secureSupport
      : null;


      
  useWindowSecure(contentProtection && secureSupport === "supported");

  const lessons = useMemo(() => normalizeLessons(lessonsRes?.data), [lessonsRes?.data]);
  const currentLessonIndex = lessons.findIndex(
    (lesson) => lesson.id === Number(lessonId),
  );
  const currentLesson =
    currentLessonIndex >= 0 ? lessons[currentLessonIndex] : null;
  const nextLesson =
    currentLessonIndex >= 0 ? lessons[currentLessonIndex + 1] : undefined;
  const previousLesson =
    currentLessonIndex >= 0 ? lessons[currentLessonIndex - 1] : undefined;

  // Sub-line of the desktop header: the course this lesson belongs to, and
  // where in it the reader currently is.
  const lessonPosition = [
    course?.title,
    currentLessonIndex >= 0 && lessons.length
      ? `جلسه ${(currentLessonIndex + 1).toLocaleString("fa-IR")} از ${lessons.length.toLocaleString("fa-IR")}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // Remember this lesson so the home page can offer "ادامه مطالعه" — but only
  // for courses the user has actually enrolled in / purchased. Previewing a
  // course they haven't joined should never populate the resume card.
  useEffect(() => {
    if (!course || !courseId || !lessonId || !miniAppUuid) return;
    const isEnrolled = Boolean(course.is_enrolled || course.is_purchased);
    if (!isEnrolled) {
      // Drop any stale record pointing at this not-joined course.
      if (getLastStudied()?.courseId === courseId) clearLastStudied();
      return;
    }
    const detail = courseRes?.data as
      | {
        cover_url?: string | null;
        external_media?: { media_token?: string | null };
        progress_percentage?: number | null;
      }
      | undefined;
    const coverUrl =
      detail?.cover_url || detail?.external_media?.media_token || undefined;
    setLastStudied({
      courseId,
      lessonId,
      miniAppUuid,
      courseTitle: course.title,
      lessonTitle: currentLesson?.title,
      coverUrl: coverUrl ?? undefined,
      lessonsCount: course.lessons_count,
      progressPercentage:
        typeof detail?.progress_percentage === "number"
          ? detail.progress_percentage
          : undefined,
      updatedAt: Date.now(),
    });
  }, [
    course,
    courseId,
    lessonId,
    miniAppUuid,
    currentLesson?.title,
    courseRes?.data,
  ]);

  const forbiddenPage = contentsQuery.data?.pages?.find(
    (page) => page.status === 403,
  );
  const forbidden = Boolean(forbiddenPage);

  // Two different 403s share this endpoint: "course not purchased" (paywall)
  // and "device limit reached". An enrolled/purchased user can only be hitting
  // the latter, so we relay the server message instead of asking them to buy.
  const isDeviceLimited =
    forbidden && Boolean(course?.is_enrolled || course?.is_purchased);

  // Post-purchase content event: the user opened a lesson they can access.
  // Fire once per lesson; skip when access is forbidden (purchase CTA shown).
  const trackedLessonRef = useRef<string | null>(null);
  useEffect(() => {
    if (forbidden || !course || !currentLesson) return;
    if (!miniAppUuid || !courseId || !lessonId) return;
    if (trackedLessonRef.current === lessonId) return;
    trackedLessonRef.current = lessonId;
    trackLessonStarted({
      course_id: Number(courseId),
      lesson_id: Number(lessonId),
      mini_app_uuid: miniAppUuid,
      lesson_title: currentLesson.title,
      lesson_order: currentLesson.sort_order ?? undefined,
    });
  }, [forbidden, course, currentLesson, miniAppUuid, courseId, lessonId]);
  const contents = useMemo(
    () =>
      (contentsQuery.data?.pages ?? [])
        .filter((page) => page.status !== 403 && page.success)
        .flatMap((page) => normalizeContents(page.data)),
    [contentsQuery.data],
  );

  const handleLoadMore = async () => {
    setIsFetchingMoreManually(true);
    try {
      await contentsQuery.fetchNextPage();
    } finally {
      setIsFetchingMoreManually(false);
    }
  };

  // Protected course on a client that cannot enforce the capture block: the
  // content is withheld entirely — even from purchased users — because
  // rendering it would leave it fully capturable. Checked before the paywall
  // so nobody buys a course they cannot watch on this device.
  if (protectionBlockReason) {
    return (
      <div dir="rtl" className="flex min-h-svh flex-col bg-background">
        <TopBar
          altHref={backHref}
          title={currentLesson?.title || "محتوای جلسه"}
          sticky
        />
        <ProtectedContentNotice reason={protectionBlockReason} />
      </div>
    );
  }

  // Locked lesson: show the paywall notice + sticky purchase CTA, nothing else.
  // Handling it here keeps the main view below free of `forbidden` branching.
  if (forbidden) {
    return (
      <div dir="rtl" className="flex min-h-svh flex-col bg-background">
        <TopBar
          altHref={backHref}
          title={currentLesson?.title || "محتوای جلسه"}
          subtitle={course?.title}
          className={LESSON_BAR_CLASS}
          sticky
        />
        {/* Which 403 this is depends on enrollment, so wait for the course
            query rather than flashing the paywall at a paying user. */}
        {isCourseLoading ? (
          <div className="flex-1" />
        ) : isDeviceLimited ? (
          <LessonDeviceLimitNotice
            message={forbiddenPage?.message}
            backHref={backHref}
          />
        ) : (
          <LessonLockedNotice />
        )}
        {course && !isDeviceLimited && !isCourseLoading ? (
          <CoursePurchaseCta
            username={course.mini_app?.miniapp_eitaa_username}
            miniAppUuid={miniAppUuid}
            courseId={course.id}
            navigateToHref={`/course/${course.id}/lesson/${lessonId}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`}
            isFree={
              course.is_free ?? (course.special_price ?? course.price ?? 0) === 0
            }
            isPurchased={course.is_purchased}
            isEnrolled={course.is_enrolled}
            price={course.price}
            specialPrice={course.special_price}
            title={course.title}
            lessonsCount={course.lessons_count}
            usersCount={course.users_count}
            isUnavailable={course.settings?.availability_status === 2}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div dir="rtl" className="flex min-h-svh flex-col bg-background">
      <TopBar
        altHref={backHref}
        title={currentLesson?.title || "محتوای جلسه"}
        subtitle={lessonPosition}
        className={LESSON_BAR_CLASS}
        sticky
        actions={
          <LessonNavigation
            miniAppUuid={miniAppUuid}
            courseId={courseId}
            nextLesson={nextLesson}
            previousLesson={previousLesson}
            className="hidden shrink-0 items-center gap-2 lg:flex"
          />
        }
      />

      <div className="flex-1 lg:px-6 lg:pb-8">
        {contentsQuery.isLoading ? (
          <div className="space-y-4 px-4 lg:px-0">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-24 animate-pulse rounded-xl bg-content3"
              />
            ))}
          </div>
        ) : (
          <>
            {view === "v2" ? (
              <LessonContentsV2
                contents={contents}
                lessonTitle={currentLesson?.title}
                analyticsContext={{
                  miniappId: miniAppUuid,
                  courseId: courseId ? Number(courseId) : undefined,
                  lessonId: lessonId ? Number(lessonId) : undefined,
                }}
                hasNextPage={contentsQuery.hasNextPage}
                isFetchingMore={
                  isFetchingMoreManually || contentsQuery.isFetchingNextPage
                }
                onLoadMore={handleLoadMore}
              />
            ) : (
              <LessonContentsV1
                contents={contents}
                lessonTitle={currentLesson?.title}
                analyticsContext={{
                  miniappId: miniAppUuid,
                  courseId: courseId ? Number(courseId) : undefined,
                  lessonId: lessonId ? Number(lessonId) : undefined,
                }}
              />
            )}

            {/* v2 carries its own "load more" at the foot of the playlist, so
                this one belongs to the v1 stack alone. */}
            {view === "v1" && contentsQuery.hasNextPage ? (
              <div className="px-4 pb-4">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={
                    isFetchingMoreManually || contentsQuery.isFetchingNextPage
                  }
                  className="w-full rounded-lg bg-content3 p-3 text-sm font-medium text-default-700 disabled:opacity-70"
                >
                  {isFetchingMoreManually || contentsQuery.isFetchingNextPage
                    ? "در حال بارگذاری..."
                    : "نمایش محتوای بیشتر"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Phone-only: on desktop the header carries the lesson nav, and the view
          toggle has nothing left to toggle between. */}
      {!contentsQuery.isLoading ? (
        <div className="lg:hidden">
          {view === "v2" ? null : <div className="h-32" />}

          <div className="fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-xl px-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={() =>
                handleChangeContentView(contentView === "v1" ? "v2" : "v1")
              }
              aria-label="تغییر حالت نمایش"
              className="self-end flex size-12 items-center justify-center rounded-full bg-primary text-white shadow-card transition-transform active:scale-95"
            >
              <Icon
                name={CONTENT_VIEW_ICON[contentView === "v1" ? "v2" : "v1"]}
                className="size-5"
              />
            </button>
            <LessonNavigation
              miniAppUuid={miniAppUuid}
              courseId={courseId}
              nextLesson={nextLesson}
              previousLesson={previousLesson}
            />
          <div className="safe-area-inset-bottom"></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
